import { useEffect, useRef } from 'react';
import { AppState, Platform } from 'react-native';
import { supabase } from '../../shared/supabase/supabaseClient';

type Opts = {
  /** Poll while delivery/matching may change without a clean WS event. */
  pollMs?: number | null;
};

/**
 * Realtime for pedido online detail — critical ops surface.
 * Tables: order + changes + events + assignment + tracking.
 * setAuth required for RLS-filtered postgres_changes.
 * Poll fallback when WS drops or events are missed.
 */
export function useOrderDetailRealtime(
  orderId: string | null,
  onChanged: () => void,
  opts?: Opts,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const visibleRef = useRef(true);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const pollMs = opts?.pollMs === undefined ? 30_000 : opts.pollMs;

  useEffect(() => {
    if (!orderId) return;

    const scheduleRefresh = () => {
      if (!visibleRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onChangedRef.current();
      }, 350);
    };

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;

    void (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token || cancelled) return;
      await supabase.realtime.setAuth(session.access_token);
      if (cancelled) return;

      channel = supabase
        .channel(`order-detail-${orderId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'customer_orders', filter: `id=eq.${orderId}` },
          scheduleRefresh,
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'customer_order_item_changes',
            filter: `order_id=eq.${orderId}`,
          },
          scheduleRefresh,
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'customer_order_events',
            filter: `order_id=eq.${orderId}`,
          },
          scheduleRefresh,
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'delivery_assignments',
            filter: `order_id=eq.${orderId}`,
          },
          scheduleRefresh,
        )
        // GPS lives in MerchantDeliveryTrackingPanel — avoid full order reload per ping
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            scheduleRefresh();
          }
        });
    })();

    if (pollMs && pollMs > 0) {
      pollRef.current = setInterval(() => {
        if (visibleRef.current) onChangedRef.current();
      }, pollMs);
    }

    const appSub = AppState.addEventListener('change', (state) => {
      visibleRef.current = state === 'active';
      if (state === 'active') scheduleRefresh();
      if (state !== 'active' && timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    });

    let removeVisibility: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVisibility = () => {
        visibleRef.current = document.visibilityState === 'visible';
        if (visibleRef.current) scheduleRefresh();
        if (!visibleRef.current && timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      visibleRef.current = document.visibilityState === 'visible';
      removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    }

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
      if (channel) void supabase.removeChannel(channel);
      appSub.remove();
      removeVisibility?.();
    };
  }, [orderId, pollMs]);
}
